import * as React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Check, Sparkles, Code2, ShieldCheck, Heart, Globe, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { isHerikaPlatform } from "@/auth/keycloak";
import { useMyAccess } from "@/hooks/useMyAccess";

type Mode = "ops" | "dev" | "admin" | "crm" | "platform";

const MODES: Record<
  Mode,
  {
    label: string;
    tag: string;
    dot: string;
    icon: LucideIcon;
    href: string;
    tagline: string;
    disabled?: boolean;
  }
> = {
  ops: {
    label: "herika",
    tag: "Operativo",
    dot: "bg-accent",
    icon: Sparkles,
    href: "/",
    tagline: "Travel retail OS · agenzia",
  },
  admin: {
    label: "herika admin",
    tag: "Admin",
    dot: "bg-indigo-500",
    icon: ShieldCheck,
    href: "/admin",
    tagline: "Gestione azienda travel",
  },
  dev: {
    label: "herika dev",
    tag: "Developer",
    dot: "bg-emerald-400",
    icon: Code2,
    href: "/dev",
    tagline: "Infrastruttura, AI, integrazioni",
  },
  crm: {
    label: "herika crm",
    tag: "CRM",
    dot: "bg-rose-400",
    icon: Heart,
    href: "/crm",
    tagline: "Clienti, lead e campagne",
  },
  platform: {
    label: "herika platform",
    tag: "Platform",
    dot: "bg-sky-500",
    icon: Globe,
    href: "/platform",
    tagline: "Console multi-tenant · feature flag",
  },
};

const MODE_ORDER: Mode[] = ["ops", "admin", "dev", "crm", "platform"];

interface Props {
  agencyName: string;
  dark?: boolean;
}

function detectMode(pathname: string): Mode {
  if (pathname.startsWith("/platform")) return "platform";
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/dev")) return "dev";
  if (pathname.startsWith("/crm")) return "crm";
  return "ops";
}

export function BrandSwitcher({ agencyName, dark = false }: Props) {
  const [open, setOpen] = React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const ref = React.useRef<HTMLDivElement>(null);

  const mode = detectMode(location.pathname);
  const cur = MODES[mode];

  // Unified per-area gating: admin/dev are visible when the ORG has the area
  // module entitled AND the user holds the area client-role (both from
  // /me/access). "platform" keeps the Henoia realm-role. The legacy realm role
  // herika-admin is no longer consulted.
  const { canArea } = useMyAccess();
  const visibleModes = React.useMemo<Mode[]>(() => {
    const platform = isHerikaPlatform();
    return MODE_ORDER.filter((m) => {
      if (m === "admin") return canArea("admin");
      if (m === "dev") return canArea("dev");
      if (m === "crm") return canArea("crm");
      if (m === "platform") return platform;
      return true;
    });
  }, [canArea]);

  React.useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const display = mode === "ops" ? agencyName || "herika" : MODES[mode].label;

  return (
    <div ref={ref} className="pointer-events-auto relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex items-center gap-2 rounded-full px-1.5 py-1 transition-opacity hover:opacity-70",
          dark ? "text-white" : "text-foreground",
        )}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className={cn("block h-1.5 w-1.5 rounded-full", cur.dot)} />
        <span className="font-heading text-[10.5px] uppercase tracking-[0.24em] font-medium">
          {display}
        </span>
        <svg
          className={cn(
            "h-2.5 w-2.5 opacity-50 transition-transform",
            open && "rotate-180",
          )}
          viewBox="0 0 10 6"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M1 1l4 4 4-4" />
        </svg>
      </button>

      {open && (
        <div
          className={cn(
            "absolute left-0 top-full z-40 mt-2 w-[260px] overflow-hidden rounded-[16px]",
            "animate-riseIn",
            dark
              ? "bg-[#15161e]/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.06)_inset,0_24px_60px_-18px_rgba(0,0,0,0.6)]"
              : "bg-white/95 backdrop-blur-2xl shadow-[0_1px_0_rgba(255,255,255,0.7)_inset,0_18px_50px_-14px_rgba(15,15,15,0.18)]",
          )}
        >
          <div
            className={cn(
              "px-4 py-2.5 text-[9px] uppercase tracking-[0.22em]",
              dark ? "text-white/40" : "text-foreground/45",
            )}
          >
            Modalità
          </div>
          {visibleModes.map((k) => {
            const m = MODES[k];
            const active = k === mode;
            const Icon = m.icon;
            return (
              <button
                key={k}
                type="button"
                onClick={() => {
                  setOpen(false);
                  navigate(m.href);
                }}
                className={cn(
                  "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
                  dark ? "hover:bg-white/[0.04]" : "hover:bg-foreground/[0.03]",
                )}
              >
                <span
                  className={cn(
                    "grid h-8 w-8 shrink-0 place-items-center rounded-[10px]",
                    dark
                      ? "bg-white/[0.05] text-white/70"
                      : "bg-foreground/[0.04] text-foreground/70",
                  )}
                >
                  <Icon className="h-[15px] w-[15px]" strokeWidth={1.5} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className={cn("h-1 w-1 rounded-full", m.dot)} />
                    <span
                      className={cn(
                        "text-[12.5px] font-medium tracking-tight",
                        dark ? "text-white" : "text-foreground",
                      )}
                    >
                      {m.label}
                    </span>
                    {active && (
                      <Check
                        className={cn(
                          "ml-auto h-3 w-3",
                          dark ? "text-white/70" : "text-foreground/60",
                        )}
                        strokeWidth={2}
                      />
                    )}
                  </div>
                  <p
                    className={cn(
                      "mt-0.5 text-[11px] leading-snug",
                      dark ? "text-white/45" : "text-foreground/50",
                    )}
                  >
                    {m.tagline}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
